import type { UploadLayout, UploadRule } from '@erp/rpc';

import { evaluateUploadRules, type RulesSummary } from './evaluateUploadRules';
import { resultsToUpload, type ResultsToUploadResult } from './resultsToUpload';
import type { ResultsSheet } from './sheetCells';

type OkResult = Extract<ResultsToUploadResult, { status: 'ok' }>;

export interface Conversion {
  /** The supplier layout the user chose for the file. */
  layout: UploadLayout | null;
  result: ResultsToUploadResult | null;
  /** Set when the file converted: the upload and how it fares on the rules. */
  checked: { upload: OkResult; summary: RulesSummary } | null;
}

const NOTHING: Conversion = { layout: null, result: null, checked: null };

/**
 * Everything the page shows about a dropped file, derived from it and the
 * supplier the user chose for it: the conversion and the rule check. Nothing is
 * guessed from the file's columns — the supplier is always the user's choice.
 * Pure, so switching supplier or editing a rule simply re-derives it.
 */
export function deriveConversion(input: {
  sheet: ResultsSheet | null;
  /** The supplier's second file, for a layout that reads one. */
  lookupSheet?: ResultsSheet | null;
  /** The suppliers on offer (see `availableSupplierLayouts`). */
  layouts: UploadLayout[];
  pickedLayoutId: string | null;
  rules: UploadRule[];
}): Conversion {
  const layout = input.layouts.find(({ id }) => id === input.pickedLayoutId);
  if (!input.sheet || !layout) return NOTHING;
  const result = resultsToUpload(
    input.sheet,
    layout.definition,
    input.lookupSheet ?? null,
  );
  const checked =
    result.status === 'ok'
      ? {
          upload: result,
          summary: evaluateUploadRules(result.lines, input.rules, layout.id),
        }
      : null;
  return { layout, result, checked };
}
